import { onUnmounted, ref } from 'vue';

import {
  JOB_EDIT_LOCK_COUNTDOWN_DURATION_DEV,
  JOB_EDIT_LOCK_COUNTDOWN_DURATION_PROD,
  JOB_EDIT_LOCK_TIMEOUT_DEV,
  JOB_EDIT_LOCK_TIMEOUT_PROD,
} from '@/utils/PortalConstants';
import { useDialogStore } from '@/stores/useDialogStore';
import { useJobEditStore } from '@/stores/useJobEditStore';

export function useJobEditLockTimeout() {
  const jobEditStore = useJobEditStore();
  const dialogStore = useDialogStore();

  const remainingSeconds = ref(0);
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  const timeoutDuration =
    import.meta.env.VITE_APP_SENTRY_ENVIRONMENT === 'production'
      ? JOB_EDIT_LOCK_TIMEOUT_PROD
      : JOB_EDIT_LOCK_TIMEOUT_DEV;

  const countdownDuration =
    import.meta.env.VITE_APP_SENTRY_ENVIRONMENT === 'production'
      ? JOB_EDIT_LOCK_COUNTDOWN_DURATION_PROD
      : JOB_EDIT_LOCK_COUNTDOWN_DURATION_DEV;

  function startTimeout(): void {
    clearTimeout();
    timeoutId = setTimeout(() => {
      remainingSeconds.value = countdownDuration;
      startCountdown();
      dialogStore
        .open('jobEditLockTimeout', {
          isConfirmLoading: false,
          remainingSeconds: remainingSeconds.value,
        })
        .confirmed(async () => {
          await handleContinue();
        })
        .canceled(async () => {
          await handleCancel();
        });
    }, timeoutDuration);
  }

  function startCountdown(): void {
    if (countdownIntervalId) {
      window.clearInterval(countdownIntervalId);
    }
    countdownIntervalId = setInterval(() => {
      remainingSeconds.value -= 1;
      if (dialogStore.jobEditLockTimeout.isDialog) {
        dialogStore.jobEditLockTimeout.dialogData.remainingSeconds =
          remainingSeconds.value;
      }
      if (remainingSeconds.value <= 0) {
        clearCountdown();
      }
    }, 1000);
  }

  function clearCountdown(): void {
    if (countdownIntervalId) {
      window.clearInterval(countdownIntervalId);
      countdownIntervalId = null;
    }
  }

  function clearTimeout(): void {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    clearCountdown();
  }

  async function handleCancel(): Promise<void> {
    clearTimeout();

    jobEditStore.lockState.isLocked = false;
    jobEditStore.lockState.lockedBy = null;
  }

  async function handleContinue(): Promise<void> {
    dialogStore.jobEditLockTimeout.dialogData.isConfirmLoading = true;
    clearTimeout();

    try {
      jobEditStore.lockState.isLocked = false;
      await jobEditStore.lockJob();

      startTimeout();
    } finally {
      dialogStore.jobEditLockTimeout.dialogData.isConfirmLoading = false;
    }
  }

  onUnmounted(() => {
    clearTimeout();
    clearCountdown();
  });

  return {
    startTimeout,
    clearTimeout,
  };
}
